<script>
    import {onMount} from "svelte";
    import Progressbar from "../components/Progressbar.svelte";
    import {modelsArchiveDownloadUrl, modelsUrl} from "../urls";
    import {customFetch} from "../customFetch";
    import Transition from "../components/Transition.svelte";
    import {notifications} from "../notifications.js";
    import {parseFbx} from "../modelUtils.js";
    import {crc32FromArrayBuffer} from "../Uploader/crc32";
    import {
        AnimationsMissingStatus,
        DataChangedStatus,
        DuplicateProblemStatus,
        ScaleProblemStatus,
        ServerMetadataEmptyStatus,
        TagsChangedStatus,
        TextureMapProblemStatus,
        UploadStatus,
        MissingLodIndexesProblemStatus,
        NotLodMeshesFoundProblemStatus,
    } from "../Uploader/statuses";
    import {UploadTask, UploadTaskAction} from "../Uploader/uploadTask";

    let isLoading = true;
    let isFileProcessing = false;
    let fileProcessingCurrent = 0;
    let fileProcessingTotal = 1;
    let isUploadComplete = false;

    let needUploadNew = true;
    let needUploadModified = true;
    let needUploadDeleting = true;
    let showWarnings = false;
    let forceParse = false;

    $: filteredModels = clientModels.filter((el) => {
        switch (el.Action) {
            case UploadTaskAction.Create:
                return needUploadNew;
            case UploadTaskAction.Update:
                return needUploadModified;
            case UploadTaskAction.Delete:
                return needUploadDeleting;
            case UploadTaskAction.None:
                return showWarnings && el.Status.length > 0;
            default:
                return true;
        }
    });

    onMount(() => {
        getServerModels();
    });

    let input;
    let serverModels = null;
    let clientModels = [];

    async function getServerModels() {
        isLoading = true;
        try {
            let response = await customFetch(modelsUrl());

            let data = await response.json();
            serverModels = data.reduce((map, elem) => {
                map[normalizeName(elem.Filename)] = elem;

                return map;
            }, {});
        } catch (e) {
            notifications.danger(
                `Can't get models list from the server: ${e.message}`
            );
        } finally {
            isLoading = false;
        }
    }

    function getServerFile(name) {
        return serverModels[normalizeName(name)];
    }

    function normalizeName(name) {
        return name.toLowerCase().replace(" ", "");
    }

    function handleFile(file, binaryData) {
        let path = file.webkitRelativePath;

        let result = new UploadTask();
        result.File = file;
        result.Filename = path.replace(/^.*[\\\/]/, "");
        result.Crc = crc32FromArrayBuffer(binaryData);
        result.Tags = path
            .replace("\\", "/")
            .split("/")
            .slice(1, -1);

        let serverFile = getServerFile(result.Filename);
        let needParseMetadata = forceParse || !serverFile || serverFile.Crc !== result.Crc || !serverFile.Metadata || !JSON.parse(serverFile.Metadata).Animations || JSON.stringify(file.Tags) !== JSON.stringify(serverFile.Tags);
        if (needParseMetadata) {
            result.ParseResult = parseFbx(binaryData);
        }

        return result;
    }

    function customSort(a, b) {
        let result;

        if (a.status === UploadStatus.Skip) {
            result = -1;
        } else if (b.status === UploadStatus.Skip) {
            result = 1;
        } else if (a.status === UploadStatus.Create) {
            result = -1;
        } else if (b.status === UploadStatus.Create) {
            result = 1;
        } else {
            result = 0;
        }

        console.log('Comparing:', a, b, 'Returning:', result);
        return result;
    }

    async function handleFilesChanged(event) {
        isFileProcessing = true;
        const files = Array.from(event.target.files)
            .filter((f) => f.webkitRelativePath.toLowerCase().endsWith(".fbx"));
        fileProcessingCurrent = 0;
        fileProcessingTotal = files.length;
        let results = await readFiles(files, () => fileProcessingCurrent++);
        analyzeFiles(results);
        clientModels = results;
        console.log(clientModels);
        isFileProcessing = false;
    }

    async function readFiles(files, onReadFile) {
        const concurrencyLimit = 8;
        let activePromises = [];
        let results = [];

        const enqueue = (file) => {
            const promise = new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = handleFile(file, reader.result);
                    resolve(result);
                    onReadFile();
                };

                reader.onerror = () => reject(reader.error);
                reader.readAsArrayBuffer(file);
            });

            if (activePromises.length >= concurrencyLimit) {
                const earliestPromise = Promise.race(activePromises);
                earliestPromise.then(() => {
                    activePromises = activePromises.filter(p => p !== earliestPromise);
                    activePromises.push(promise);
                });
            } else {
                activePromises.push(promise);
            }

            return promise;
        };

        for (let file of files) {
            results.push(await enqueue(file));
        }

        await Promise.all(activePromises);

        return results;
    }

    function analyzeFiles(results) {
        let found = [];

        let existingNames = {};

        for (let i = 0; i < results.length; i++) {
            let file = results[i];
            let fileName = file.Filename;
            let serverFile = getServerFile(fileName);

            if (file.ParseResult) {
                if (file.ParseResult.problemScaleMeshes.length > 0)
                    file.AddStatus(new ScaleProblemStatus(file.ParseResult.problemScaleMeshes));

                if (Object.keys(file.ParseResult.problemImages).length > 0)
                    file.AddStatus(new TextureMapProblemStatus(file.ParseResult.problemImages));

                if (file.ParseResult.missingLodIndexes.length > 0)
                    file.AddStatus(new MissingLodIndexesProblemStatus(file.ParseResult.missingLodIndexes));

                if (file.ParseResult.notLodMeshes.length > 0)
                    file.AddStatus(new NotLodMeshesFoundProblemStatus(file.ParseResult.notLodMeshes))
            }

            let normalizedName = normalizeName(fileName);
            if (serverFile) {
                let isContentChanged;
                if (JSON.stringify(file.Tags) !== JSON.stringify(serverFile.Tags)) {
                    file.AddStatus(new TagsChangedStatus(serverFile.Tags, file.Tags));
                    isContentChanged = true;
                }
                if (serverFile.Crc !== file.Crc) {
                    file.AddStatus(new DataChangedStatus());
                    isContentChanged = true;
                }
                if (!serverFile.Metadata) {
                    file.AddStatus(new ServerMetadataEmptyStatus())
                    isContentChanged = true;
                }
                else if (!JSON.parse(serverFile.Metadata).Animations) {
                    file.AddStatus(new AnimationsMissingStatus())
                    isContentChanged = true;
                }

                file.Action = isContentChanged ? UploadTaskAction.Update : UploadTaskAction.None;
                file.ID = serverFile.ID;

                found[normalizedName] = true;
            } else {
                file.Action = UploadTaskAction.Create;
            }

            if (!existingNames[normalizedName]) {
                existingNames[normalizedName] = file;
            } else {
                file.AddStatus(new DuplicateProblemStatus(existingNames[normalizedName].File?.webkitRelativePath));
            }

            if (file.Status.some((status) => status.isCritical()))
                file.Action = UploadTaskAction.Skip;
        }

        let toRemove = Object.values(serverModels).filter(
            (elem) => !found[normalizeName(elem.Filename)]
        );

        for (let elem of toRemove) {
            let removeRow = new UploadTask();
            removeRow.ID = elem.ID;
            removeRow.Action = UploadTaskAction.Delete;
            removeRow.Filename = elem.Filename;
            results.push(removeRow);
        }
    }

    function getMetadataFromParseResult(parseResult) {
        return JSON.stringify({
            Width: parseResult.size.x / 100,
            Height: parseResult.size.z / 100,
            Tallness: parseResult.size.y / 100,
            MaterialsMap: parseResult.usedImages,
            Animations: parseResult.animations.map(item => ({
                Name: item.name,
                Duration: item.duration,
            })),
        });
    }

    async function upload() {
        isFileProcessing = true;
        fileProcessingTotal = filteredModels.length;

        for (let i = 0; i < filteredModels.length; i++) {
            let current = filteredModels[i];
            let action = current.Action
            if (action === UploadTaskAction.None || action === UploadTaskAction.Skip) continue;

            let request = {}
            let url = modelsUrl()
            if (action === UploadTaskAction.Delete) {
                request.method = "DELETE";
                url += `/${current.ID}`;
            } else {
                request.method = "POST";
                let formData = new FormData();

                if (action === UploadTaskAction.Create || action === UploadTaskAction.Update) {
                    formData.set("model_data", current.File);

                    if (current.ParseResult) {
                        formData.set("model_metadata", getMetadataFromParseResult(current.ParseResult))
                    }
                }

                formData.set("row_body", JSON.stringify({
                    Tags: current.Tags,
                }))

                if (action === UploadTaskAction.Update) {
                    url += `/${current.ID}`;
                }
                request.body = formData;
            }

            try {
                await customFetch(url, request);
                current.ClearStatus();
                current.AddStatus(new UploadStatus(true, "OK"))
            } catch (e) {
                current.AddStatus(new UploadStatus(false, e.message))
            }

            fileProcessingCurrent = i;
        }

        isFileProcessing = false;
        isUploadComplete = true;
    }

    async function reset() {
        clientModels = [];
        isUploadComplete = false;
        input.value = "";
        await getServerModels()
    }
</script>

<Transition>
    <div class="content-container">
        {#if isLoading}
            <span class="animate-pulse">Loading...</span>
        {:else if isFileProcessing}
            Processing files...
            <Progressbar
                current={fileProcessingCurrent}
                total={fileProcessingTotal}
            />
        {:else if serverModels != null}
            <input
                type="file"
                class="hidden"
                multiple
                webkitdirectory
                bind:this={input}
                on:change={handleFilesChanged}
            />

            {#if clientModels.length > 0}
                <div class="flex mb-4 items-baseline">
                    <div class="align-baseline px-2">
                        Filter:
                        <label for="upload_new">New
                            <input type="checkbox" id="upload_new" bind:checked={needUploadNew}/></label>
                        <label for="upload_changed">Changed
                            <input type="checkbox" id="upload_changed" bind:checked={needUploadModified}/></label>
                        <label for="upload_removing">Removing <input type="checkbox" id="upload_removing"
                                                                    bind:checked={needUploadDeleting}/></label>
                        <label for="show_warnings">Warnings <input type="checkbox" id="show_warnings"
                                                                     bind:checked={showWarnings}/></label>

                    </div>
                    <div class="px-4">
                        {#if filteredModels.length > 0 && !isUploadComplete}
                            <button class="btn-primary" on:click={upload}>Upload changes</button>
                        {/if}
                        <button class="btn-secondary" on:click={reset}>Reset</button>
                    </div>
                </div>
                {#if filteredModels.length > 0}
                    <table class="table-auto w-full">
                        <thead class="sticky">
                        <tr>
                            <th>Name</th>
                            <th>Action</th>
                            <th>Status</th>
                            <th>Tags</th>
                            <th>Path</th>
                        </tr>
                        </thead>
                        <tbody>
                        {#each Object.values(filteredModels) as row, i}
                            <tr>
                                <td>{row.Filename}</td>
                                <td>{row.Action}</td>
                                <td>{@html row.GetStatusString()}</td>
                                <td>{row.Tags}</td>
                                <td>{row.File?.webkitRelativePath}</td>
                            </tr>
                        {/each}
                        </tbody>
                    </table>
                {:else}
                    Filtered table contains no rows
                {/if}
            {:else}
                <button class="btn-primary" on:click={input.click()}>Select root folder with models</button>
                or <a href={modelsArchiveDownloadUrl()}>Download archive</a>
                <br/>
                <label><input type="checkbox" bind:value={forceParse}> Force models parsing (slow, but can show some warnings)</label>
            {/if}
        {/if}
    </div>
</Transition>
